// TabEntranceControl.js
import React, { useEffect, useMemo }  from 'react';
import { Row, Col, Form, Switch, Input, InputNumber, Select, Tooltip, Typography, ColorPicker } from 'antd';
import {
    VerticalAlignTopOutlined, VerticalAlignBottomOutlined, RetweetOutlined, InfoCircleOutlined,
} from '@ant-design/icons';

const { Text } = Typography;

// Preset colors for entrance control feedback
const deniedPresets = [
    { label: 'Transparent', colors: ['#FFFFFF'] },
    { label: 'Denied', colors: ['#C62828', '#EF5350', '#EF9A9A', '#F5C6CB', '#F8D7DA', '#FDECEA'] },
];

const successPresets = [
    { label: 'Transparent', colors: ['#FFFFFF'] },
    { label: 'Success', colors: ['#1B5E20', '#2E7D32', '#66BB6A', '#81C784', '#D4EDDA', '#E8F5E9'] },
];

// Helper that wraps any label with an info tooltip.
const labelWithInfo = (label, tooltip) => (
    <span>
    {label}
        <Tooltip title={tooltip}>
      <InfoCircleOutlined style={{ marginLeft: 8, color: '#999' }} />
    </Tooltip>
  </span>
);

const TabEntranceControl = ({ showEFN }) => {
    // grab the parent form instance and watch for changes.
    const form = Form.useFormInstance();
    const selectedProgrammePointId = Form.useWatch('SelectedProgrammePointId', form);

    const resolvedPPId = useMemo(() => {
        return selectedProgrammePointId ?? form.getFieldValue('SelectedProgrammePointId');
    }, [selectedProgrammePointId, form]);

    useEffect(() => {
        // Wait until SelectedProgrammePointId is loaded and has changed.
        if (selectedProgrammePointId === undefined) return;

        const current = form.getFieldValue('CreateLeadImmediately');

        // Reset only if undefined or null, never if already true/false
        if (current === undefined || current === null) {
            form.setFieldsValue({ CreateLeadImmediately: false });
        }

    }, [selectedProgrammePointId]);

    return (
        <>
        {/* Tab description */}
        <Row style={{ marginBottom: 16 }}>
            <Col span={24}>
                <Text type="secondary">
                    Configure the behaviour of scanners on devices assigned to this configuration.
                </Text>
            </Col>
        </Row>
        <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={8}>
                <Form.Item name="Direction"
                   label={labelWithInfo(
                       'Direction',
                       'Choose if the scanner registers IN, OUT or PASS events'
                   )}
                   wrapperCol={{ span: 6 }}>
                    <Select placeholder="Select direction" style={{ width: '100%' }}>
                        <Select.Option value="IN">
                            <VerticalAlignTopOutlined style={{ color: 'green', fontSize: '20px' }} />
                        </Select.Option>
                        <Select.Option value="OUT">
                            <VerticalAlignBottomOutlined style={{ color: 'red', fontSize: '20px' }} />
                        </Select.Option>
                        <Select.Option value="PASS">
                            <RetweetOutlined style={{ color: 'turquoise', fontSize: '20px' }} />
                        </Select.Option>
                    </Select>
                </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8}>
                <Tooltip
                    title={
                        !resolvedPPId
                            ? 'Select a Programme Point in order to activate this option.'
                            : ''
                    }
                    color="#faad14"
                    placement="right"
                >
                    <Form.Item
                        label={labelWithInfo(
                            'Create Lead Immediately',
                            'Generate a lead as soon as the QR code is scanned',
                        )}
                        name="CreateLeadImmediately_mobile"
                        valuePropName="checked"
                    >
                        <Switch disabled={!resolvedPPId} />
                    </Form.Item>
                </Tooltip>
            </Col>
            <Col xs={24} sm={12} md={8}>
                <Form.Item
                    name="AutoCheckInMilliSeconds"
                    label={labelWithInfo(
                        'Delay Milliseconds',
                        'Time delay before automatic check-in (in ms)'
                    )}
                    wrapperCol={{ span: 6 }}
                >
                    <InputNumber style={{ width: '100%' }} />
                </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8}>
                <Form.Item
                    name="IsLineaProEnabled"
                    valuePropName="checked"
                    label={labelWithInfo(
                        'Linea Pro',
                        'Enable support for Linea Pro scanners'
                    )}
                >
                    <Switch />
                </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8}>
                <Form.Item
                    name="IsCameraEnabled"
                    valuePropName="checked"
                    label={labelWithInfo(
                        'Camera',
                        'Allow the built-in camera to be used as a scanner'
                    )}
                >
                    <Switch />
                </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8}>
                <Form.Item
                    name="IsPresentCountEnabled"
                    valuePropName="checked"
                    label={labelWithInfo(
                        'Presence Count',
                        'Display the current number of people inside the venue'
                    )}
                >
                    <Switch />
                </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8}>
                <Form.Item
                    name="IsShowParticipantNameEnabled"
                    valuePropName="checked"
                    label={labelWithInfo(
                        'Show Participant Name',
                        'Show the participant’s name after scanning (necessary to display the CustomFields)'
                    )}
                >
                    <Switch />
                </Form.Item>
            </Col>
            {showEFN && (
                <Col xs={24} sm={12} md={8}>
                    <Form.Item
                        name="IsEFNCheckEnabled"
                        valuePropName="checked"
                        label={labelWithInfo(
                            'Check EFN',
                            'Enable validation of EFN (German health professionals)'
                        )}
                    >
                        <Switch />
                    </Form.Item>
                </Col>
            )}
        </Row>
        <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={8}>
                <Form.Item
                    name="DeniedBackgroundColor"
                    label={labelWithInfo(
                        'Denied Background Color',
                        'Background color shown when access is denied'
                    )}
                    initialValue="#FFFFFF"
                >
                    <ColorPicker
                        showText
                        format="hex"
                        presets={deniedPresets}
                        disabledAlpha
                        onChange={(color) => {
                            const rgb = color?.toRgb?.();

                            // if transparent selected → force white
                            if (rgb?.a === 0) {
                                form.setFieldValue('DeniedBackgroundColor', '#FFFFFF');
                                return;
                            }

                            form.setFieldValue(
                                'DeniedBackgroundColor',
                                color?.toHexString?.() || '#FFFFFF'
                            );
                        }}
                        onClear={() => {
                            form.setFieldValue('DeniedBackgroundColor', '#FFFFFF');
                        }}
                    />
                </Form.Item>
            </Col>

            <Col xs={24} sm={12} md={8}>
                <Form.Item
                    name="SuccessBackgroundColor"
                    label={labelWithInfo(
                        'Success Background Color',
                        'Background color shown when access is granted'
                    )}
                    initialValue="#FFFFFF"
                >
                    <ColorPicker
                        showText
                        format="hex"
                        presets={successPresets}
                        disabledAlpha
                        onChange={(color) => {
                            const rgb = color?.toRgb?.();

                            if (rgb?.a === 0) {
                                form.setFieldValue('SuccessBackgroundColor', '#FFFFFF');
                                return;
                            }

                            form.setFieldValue(
                                'SuccessBackgroundColor',
                                color?.toHexString?.() || '#FFFFFF'
                            );
                        }}
                        onClear={() => {
                            form.setFieldValue('SuccessBackgroundColor', '#FFFFFF');
                        }}
                    />
                </Form.Item>
            </Col>
        </Row>
        </>
    );
};

export default TabEntranceControl;